import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault
} from 'use-query-params';

import { Button, Grid } from '@material-ui/core';
import { Visibility } from '@material-ui/icons';

import { FetchError } from '../../common/api/api';
import AppSnackbar from '../../common/AppSnackbar';
import HoveredCard from '../../common/card/HoveredCard';
import { formatDate } from '../../common/formatter';
import HeaderBackButton, {
  StyledPageTemplate
} from '../../common/HeaderBackButton';
import HeaderTitle from '../../common/HeaderTitle';
import PageTemplateAlert from '../../common/page-template/PageTemplateAlert';
import PageTemplateDashboard from '../../common/page-template/PageTemplateDashboard';
import PageTemplateHeader, {
  PageTemplateHeaderActions
} from '../../common/page-template/PageTemplateHeader';
import TaskStatusLabel from '../../common/TaskStatusLabel';
import { useJiraConnectAPI } from '../jira-connect/JiraConnect.hooks';
import { useJiraServerContext } from '../jira-server/jira-server.context';
import SubTaskListCard from '../tasks/cards/SubTaskListCard';
import TaskAssignCard from '../tasks/cards/TaskAssignCard';
import TaskAttachmentCard from '../tasks/cards/TaskAttachmentCard';
import TaskFileListCard from '../tasks/cards/TaskFileListCard';
import TaskStatusVersionCard from '../tasks/cards/TaskStatusVersionCard';
import { Task } from '../tasks/Task.interface';
import TaskStatusActions from '../tasks/TaskStatusActions';
import { ProductBoardColumnEntity } from './board/ProductBoardColumn';
import TaskJiraIssueAssignCard from '../tasks/cards/TaskJiraIssueAssignCard';
import ProductsSubSectionComponentCard from './cards/ProductsSubSectionComponentCard';
import ProductsSubSectionReleaseCard from './cards/ProductsSubSectionReleaseCard';
import UserNeedDesignInputReferenceCard from './cards/UserNeedDesignInputReferenceCard';
import UserNeedVerificationReferenceCard from './cards/UserNeedVerificationReferenceCard';
import UserNeedValidationReferenceCard from './cards/UserNeedValidationReferenceCard';
import UserNeedInfoCard from './cards/UserNeedInfoCard';
import {
  useFindProductUserNeedsById,
  useFindProductUserNeedVersion,
  useProductEntityPrefixId
} from './Product.hooks';
import {
  BaseProductUserNeedsCardProps,
  ProductUserNeed
} from './Product.interface';
import {
  getProductEntityName,
  isProductRelationDisabled
} from './Product.config';
import { useTaskEntityFields } from '../workflow/Workflow.hooks';
import { WorkflowEntity } from '../workflow/Workflow.interface';
import environment from '../../environments';
import UserNeedDesignSpecReferenceCard from './cards/UserNeedDesignSpecReferenceCard';
import DeleteEntityCard from '../../common/cards/DeleteEntityCard';
import { useUserContext } from '../../user/User.context';
import CartIconBadge from '../../common/cart/CartIconBadge';
import TaskDueDateCard from '../tasks/cards/TaskDueDateCard';
import UserNeedHazardReferenceCard from './cards/UserNeedHazardReferenceCard';

const ProductUserNeedDetailPage: React.FC = () => {
  const { productId: paramProductId = '', userNeedId: paramUserNeedId = '' } =
    useParams<{
      productId: string;
      userNeedId: string;
    }>();
  const productId: number = parseInt(paramProductId) || 0;
  const userNeedId: number = parseInt(paramUserNeedId) || 0;
  const { hasRole } = useUserContext();
  const [from = 'detail'] = useQueryParam('from', StringParam);
  const [version] = useQueryParam('ver', withDefault(NumberParam, undefined));
  const [productUserNeed, setProductUserNeed] = useState<
    ProductUserNeed | undefined
  >();
  const [didUpdate, setDidUpdate] = useState<boolean>(false);
  const [error, setError] = useState<FetchError | undefined>();
  const { isAvailable: isJiraConnect } = useJiraConnectAPI();
  const { isJiraServer } = useJiraServerContext();
  const {
    productUserNeed: data,
    isLoadingProductUserNeeds,
    findProductUserNeedError,
    findProductUserNeed
  } = useFindProductUserNeedsById(productId, userNeedId);
  const {
    productUserNeedVersion: loadedProductUserNeedVersion,
    isLoadingProductUserNeedVersion,
    findProductUserNeedVersionError,
    findProductUserNeedVersion
  } = useFindProductUserNeedVersion(productId, userNeedId, version);
  const [productUserNeedVersion, setProductUserNeedVersion] = useState(
    loadedProductUserNeedVersion
  );

  const title: string = useProductEntityPrefixId(
    productUserNeed,
    ProductBoardColumnEntity.UserNeed
  );

  const cardProps: BaseProductUserNeedsCardProps = {
    productId,
    productUserNeed: productUserNeed!,
    productUserNeedVersion,
    onChange: (newProductUserNeed: Partial<ProductUserNeed>) => {
      if (productUserNeed) {
        // notify task components to refresh
        productUserNeed.task = { ...productUserNeed.task };
        setProductUserNeed({
          ...productUserNeed,
          ...newProductUserNeed
        });
      }
    },
    onUpdate: () => setDidUpdate(true),
    onError: (error: FetchError) => setError(error)
  };

  const handleTaskUpdate = (newTask: Partial<Task>) => {
    if (!productUserNeed?.task) {
      return;
    }
    Object.assign(productUserNeed.task, newTask);
    cardProps.onChange?.({});
    cardProps.onUpdate?.();
  };

  useEffect(() => {
    findProductUserNeed();
  }, [findProductUserNeed, productId, userNeedId]);
  useEffect(() => {
    if (version) {
      findProductUserNeedVersion();
    } else {
      setProductUserNeedVersion(undefined);
    }
  }, [findProductUserNeedVersion, productId, userNeedId, version]);
  useEffect(
    () =>
      loadedProductUserNeedVersion &&
      setProductUserNeedVersion(loadedProductUserNeedVersion),
    [loadedProductUserNeedVersion]
  );

  useEffect(() => {
    data && setProductUserNeed(data);
  }, [data]);

  useEffect(() => {
    setError(findProductUserNeedError);
  }, [findProductUserNeedError]);
  const { isFieldDisabled } = useTaskEntityFields<WorkflowEntity.UserNeed>(
    productUserNeed?.task
  );
  const task = productUserNeedVersion?.task || productUserNeed?.task;
  return (
    <StyledPageTemplate>
      <PageTemplateHeader>
        <HeaderBackButton to={`/portal/products/${productId}/${from}`} />
        <HeaderTitle>
          <h2>{isLoadingProductUserNeeds ? 'Loading...' : title}</h2>
          {productUserNeed && (
            <div className='status-container'>
              <TaskStatusLabel task={productUserNeed.task} />
            </div>
          )}
        </HeaderTitle>
        <PageTemplateHeaderActions flex>
          <CartIconBadge
            taskEntity={productUserNeed}
            onChange={productUserNeed =>
              setProductUserNeed(productUserNeed as ProductUserNeed)
            }
          />
          {productUserNeed?.task && (
            <TaskStatusActions
              task={productUserNeed.task}
              taskEntity={productUserNeed}
              onChange={(task: Task) => {
                productUserNeed.task = task;
                setProductUserNeed({ ...productUserNeed });
              }}
            />
          )}
        </PageTemplateHeaderActions>
      </PageTemplateHeader>
      {productUserNeed && version && (
        <PageTemplateAlert severity='warning'>
          Viewing previous version {version}
          {productUserNeedVersion &&
            ` from ${formatDate(productUserNeedVersion.created)}. `}
          <Link to={`./${productUserNeed.id}`}>
            Click here to view latest version
          </Link>
        </PageTemplateAlert>
      )}
      <PageTemplateDashboard>
        {productUserNeed && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <UserNeedInfoCard {...cardProps} />
                </Grid>
                {(isJiraConnect || isJiraServer) && (
                  <Grid item xs={12}>
                    <TaskJiraIssueAssignCard
                      task={productUserNeed.task}
                      entity={{
                        title: productUserNeed.title,
                        description: productUserNeed.description
                      }}
                      jiraFieldColumns={
                        environment.productConfig?.userNeed?.visibleJiraFields
                      }
                      onChange={taskJiraIssues => {
                        // update productUserNeed Task jira issues on change
                        productUserNeed.task.jiraIssues = taskJiraIssues;
                        setProductUserNeed({ ...productUserNeed });
                      }}
                    />
                  </Grid>
                )}
                {productUserNeed.task && (
                  <Grid item xs={12}>
                    <TaskAttachmentCard
                      task={productUserNeed.task}
                      disabled={Boolean(productUserNeedVersion)}
                      attachments={productUserNeedVersion?.attachments}
                      onError={cardProps.onError}
                      onChange={handleTaskUpdate}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item xs={12} md={4}>
              <Grid container spacing={3}>
                {productUserNeed.task && (
                  <>
                    <Grid item xs={12}>
                      <TaskStatusVersionCard
                        showCart
                        taskEntity={productUserNeed}
                        renderContent={group =>
                          group.version !== productUserNeed.task.version && (
                            <HoveredCard
                              title={`Version ${group.version}`}
                              content='View the previous version'
                              actions={
                                <Button
                                  variant='text'
                                  color='primary'
                                  component={Link}
                                  startIcon={<Visibility />}
                                  to={`./${productUserNeed.id}?ver=${group.version}`}
                                >
                                  View
                                </Button>
                              }
                            />
                          )
                        }
                      />
                    </Grid>
                    {task?.children && task.children.length > 0 && (
                      <Grid item xs={12}>
                        <SubTaskListCard
                          title='Stakeholders'
                          task={task}
                          disabled={
                            Boolean(version) ||
                            isFieldDisabled('assignSubTasks')
                          }
                          onError={cardProps.onError}
                          onChange={handleTaskUpdate}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <ProductsSubSectionComponentCard
                        productId={productId}
                        productSubSection={productUserNeed}
                        readOnlyComponents={
                          productUserNeedVersion &&
                          (productUserNeedVersion.components || [])
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <ProductsSubSectionReleaseCard
                        productId={productId}
                        productSubSection={productUserNeed}
                        disabled={Boolean(productUserNeedVersion)}
                        readOnlyRelease={
                          productUserNeedVersion &&
                          (productUserNeedVersion?.release || null)
                        }
                      />
                    </Grid>
                    {task && (
                      <Grid item xs={12}>
                        <TaskAssignCard task={task} />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <TaskDueDateCard
                        task={productUserNeed.task}
                        disabled={Boolean(productUserNeedVersion)}
                        onError={cardProps.onError}
                        onChange={handleTaskUpdate}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TaskFileListCard
                        task={productUserNeed.task}
                        disabled={Boolean(productUserNeedVersion)}
                        onError={cardProps.onError}
                        onChange={handleTaskUpdate}
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <UserNeedDesignInputReferenceCard {...cardProps} />
                </Grid>
                {environment.productConfig?.designSpec && (
                  <Grid item xs={12}>
                    <UserNeedDesignSpecReferenceCard {...cardProps} />
                  </Grid>
                )}
                {!isProductRelationDisabled('userNeed', 'verifications') && (
                  <Grid item xs={12}>
                    <UserNeedVerificationReferenceCard {...cardProps} />
                  </Grid>
                )}
                {!isProductRelationDisabled('userNeed', 'validations') && (
                  <Grid item xs={12}>
                    <UserNeedValidationReferenceCard {...cardProps} />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <UserNeedHazardReferenceCard {...cardProps} />
                </Grid>
                {hasRole('Delete') && (
                  <Grid item xs={12}>
                    <DeleteEntityCard
                      title={getProductEntityName('userNeed')}
                      apiPath={`products/${productId}/user-needs`}
                      entity={productUserNeed}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        )}
      </PageTemplateDashboard>
      <AppSnackbar
        severity='error'
        open={!!error}
        onClose={() => setError(undefined)}
      >
        {error?.message}
      </AppSnackbar>
      <AppSnackbar
        severity='success'
        open={didUpdate}
        onClose={() => didUpdate && setDidUpdate(false)}
      >
        Product {getProductEntityName('userNeed')} details updated
      </AppSnackbar>
    </StyledPageTemplate>
  );
};

export default ProductUserNeedDetailPage;
